import React, { useEffect, memo } from 'react'; 
import { useLeadDeliveryFormContext } from '@/context/leadDelivery'; 
import { Button } from '@/components/ui/button';
import { Save } from 'lucide-react';
import { Controller } from 'react-hook-form';
import { Switch } from '@/components/ui/switch';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Input } from '@/components/ui/input';
import { useFirebase } from '@/context/FirebaseProvider';
import BusinessHoursTable from './components/BusinessHoursTable';

interface LeadDeliverySectionProps {
  businessId: string;
  onComplete: () => void;
}

const LeadDeliverySection: React.FC<LeadDeliverySectionProps> = memo(({ 
  businessId,
  onComplete
}) => {
  const { 
    register, 
    handleSubmit, 
    control, 
    formState: { errors, isDirty, isValid },
    isLoading, 
    onSubmit,
    watch 
  } = useLeadDeliveryFormContext();
  
  const { userData } = useFirebase();
  
  // Watch for business hours toggle
  const customBusinessHours = watch("customBusinessHours");

  // Make the component data-savable attribute match the form's validity
  useEffect(() => {
    const sectionEl = document.getElementById('leadDelivery-section');
    if (sectionEl) {
      sectionEl.setAttribute('data-savable', isValid ? 'true' : 'false');
    }
  }, [isValid]);
  
  // Get user initials for avatar
  const userInitials = userData ? 
    `${userData.firstName?.[0] || ''}${userData.lastName?.[0] || ''}` : 'BO';

  if (isLoading) {
    return <div className="p-4">Loading lead delivery settings...</div>;
  }
  
  return (
    <form onSubmit={handleSubmit(onSubmit)} className="p-4 space-y-6">
      <div className="flex flex-wrap items-start gap-4">
        <div className="flex w-full gap-4">
          {/* Phone Input Section */}
          <div className="w-1/2 space-y-2">
            <label htmlFor="phone-input" className="text-sm font-medium text-gray-700">
              Send phone leads to:
            </label>
            <div className="flex items-center">
              <div className="flex-shrink-0 mr-2">
                <Avatar className="h-12 w-12">
                  <AvatarFallback className="bg-blue-100 text-blue-600">
                    {userInitials}
                  </AvatarFallback>
                </Avatar>
              </div>
              <div className="w-full">
                <Input
                  id="phone-input"
                  type="tel"
                  {...register('phone')}
                  placeholder="555-123-4567"
                  error={!!errors.phone}
                />
                {errors.phone && <p className="text-xs text-red-500 mt-1">{errors.phone.message}</p>}
                <div className="text-xs text-gray-500 mt-1">
                  Business Owner
                </div>
              </div>
            </div>
          </div>
          
          {/* Email Input Section */}
          <div className="w-1/2 space-y-2">
            <label htmlFor="email-input" className="text-sm font-medium text-gray-700">
              Send email leads to:
            </label>
            <div className="flex items-center">
              <div className="flex-shrink-0 mr-2">
                <Avatar className="h-12 w-12">
                  <AvatarFallback className="bg-blue-100 text-blue-600">
                    {userInitials}
                  </AvatarFallback>
                </Avatar>
              </div>
              <div className="w-full">
                <Input
                  id="email-input"
                  type="email"
                  {...register('email')}
                  placeholder="owner@example.com"
                  error={!!errors.email}
                />
                {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email.message}</p>}
                <div className="text-xs text-gray-500 mt-1">
                  Business Owner
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Business Hours Toggle and Table */}
      <div className="space-y-4">
        <div className="w-full py-4 flex items-center">
          <span className="text-sm font-medium text-gray-700 mr-4">Only get leads during business hours</span>
          <Controller
            name="customBusinessHours"
            control={control}
            render={({ field }) => (
              <Switch
                id="business-hours-toggle"
                checked={field.value}
                onCheckedChange={field.onChange}
                className="data-[state=checked]:bg-primary"
              />
            )}
          />
        </div>
        {customBusinessHours && (
          <div className="w-full">
            <BusinessHoursTable />
          </div>
        )}
      </div>
      
      {/* Error message */}
      {!isValid && isDirty && Object.keys(errors).length > 0 && (
         <div className="text-red-600 text-sm font-medium p-2 bg-red-50 rounded-md border border-red-200">
           Please correct the errors above before saving.
         </div>
      )}
      
      {/* Save & Continue button */}
      <div className="flex justify-end mt-6">
        <Button
          type="submit"
          disabled={!isValid || isLoading}
          className="flex items-center gap-2"
        >
          <Save size={16} />
          Save & Continue
        </Button>
      </div>
    </form>
  );
});

LeadDeliverySection.displayName = 'LeadDeliverySection';

export default LeadDeliverySection;
